import { inject } from '@angular/core';
import { Router, Routes, UrlTree } from '@angular/router';
import { AuthGuard } from '@core/auth/guards/auth.guard';
import { connectLastCompanyGuard, lastCompanyRedirectGuard } from '@core/auth/guards/last-company.guard';
import { companyPreviewResolver } from '@core/ssr/company-preview.resolver';
import { publicThemeResolver } from '@core/theme/public-theme.resolver';
import { environment } from '@environments/environment';

// data.themeSource (Anexo K.3.1): de onde vem o tema de cada rota. Manter em sincronia com BRAND/NO_COMPANY do
// script de boot do src/index.html (conferido por scripts/check-theme-boot.mjs).
// 'company' = empresa da URL; 'order' = empresa do pedido; 'last' = último tema; 'brand' = claro + roxo; 'keep' = mantém.

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [connectLastCompanyGuard],
    data: { themeSource: 'last' },
    loadComponent: () =>
      import('./pages/connect-last-company/connect-last-company.component').then((c) => c.ConnectLastCompanyComponent)
  },
  {
    path: 'agendamento/:id',
    data: { themeSource: 'order' },
    loadComponent: () => import('./pages/my-booking/my-booking.component').then((c) => c.MyBookingComponent)
  },

  // Rotas autenticadas
  {
    path: '',
    canActivateChild: [AuthGuard],
    loadComponent: () => import('./pages/home/home.component').then((c) => c.HomeComponent),
    children: [
      {
        path: 'agendamentos',
        canActivate: [lastCompanyRedirectGuard],
        data: { lastCompanyView: 'bookings', themeSource: 'last' },
        loadComponent: () => import('./pages/my-bookings/my-bookings.component').then((c) => c.MyBookingsComponent)
      },
      {
        path: 'meu-perfil',
        canActivate: [lastCompanyRedirectGuard],
        data: { lastCompanyView: 'eu', themeSource: 'last' },
        loadComponent: () => import('./pages/my-profile/my-profile.component').then((c) => c.MyProfileComponent)
      },
      {
        path: 'minhas-avaliacoes',
        data: { themeSource: 'last' },
        loadComponent: () => import('./pages/my-profile/my-reviews/my-reviews.component').then((c) => c.MyReviewsComponent)
      },
      {
        path: 'sobre-agendaja',
        data: { themeSource: 'brand' },
        loadComponent: () => import('./pages/about-agendaja/about-agendaja.component').then((c) => c.AboutAgendajaComponent)
      },
      {
        path: 'editar-perfil',
        redirectTo: 'meu-perfil',
        pathMatch: 'full'
      }
    ]
  },

  // Rotas não autenticadas que podem ter acesso de qualquer usuário.
  {
    path: '',
    children: [
      { path: 'offline', data: { themeSource: 'keep' }, loadComponent: () => import('./pages/offline/offline.component').then((c) => c.OfflineComponent) },
      { path: 'sign-in', data: { themeSource: 'brand' }, loadComponent: () => import('./pages/auth/sign-in/sign-in.component').then((c) => c.AuthSignInComponent) },
      {
        path: 'admin-login',
        data: { themeSource: 'brand' },
        loadComponent: () => import('./pages/auth/admin-login/admin-login.component').then((c) => c.AdminLoginComponent)
      },
      { path: 'sign-out', data: { themeSource: 'brand' }, loadComponent: () => import('./pages/auth/sign-out/sign-out.component').then((c) => c.AuthSignOutComponent) },
      {
        path: 'lembrete/:acao/:token',
        data: { themeSource: 'last' },
        loadComponent: () => import('./pages/reminder-action/reminder-action.component').then((c) => c.ReminderActionComponent)
      },
      {
        path: 'agenda-ja/404',
        data: { themeSource: 'brand' },
        loadComponent: () => import('./pages/not-found/not-found.component').then((c) => c.NotFoundComponent)
      },
      {
        // Página de QA do tema (os 20 tons). Só com features.publicThemeOverride; em produção vai para o 404.
        path: 'agenda-ja/temas',
        data: { themeSource: 'brand' },
        canMatch: [(): boolean | UrlTree => environment.features.publicThemeOverride || inject(Router).parseUrl('/agenda-ja/404')],
        loadComponent: () => import('./pages/theme-qa/theme-qa.component').then((c) => c.ThemeQaComponent)
      },
      {
        path: ':CompanySlug',
        data: { themeSource: 'company' },
        // A empresa é buscada uma vez por SSR (ServerCompanyService) e serve aos dois resolvers
        resolve: { publicTheme: publicThemeResolver, companyPreview: companyPreviewResolver },
        loadComponent: () => import('./core/components/company-core-page/company-core-page.component').then((c) => c.CompanyCorePageComponent),
        children: [
          {
            path: '',
            loadComponent: () => import('./pages/company/company.component').then((c) => c.CompanyComponent)
          },
          {
            path: 'pesquisar',
            loadComponent: () => import('./pages/search/search.component').then((c) => c.SearchComponent)
          },
          {
            path: 'agendar',
            children: [
              { path: '', loadComponent: () => import('./pages/booking/booking.component').then((c) => c.BookingComponent) },
              {
                path: 'revisao',
                loadComponent: () =>
                  import('./pages/booking/components/booking-summary/booking-summary.component').then((c) => c.BookingSummaryComponent)
              }
            ]
          }
        ]
      }
    ]
  }
];
